import {
  Document,
  type DocumentProps,
  Page,
  PDFViewer,
} from '@react-pdf/renderer'
import { type ReactElement, useMemo } from 'react'
import { useLocale, useTranslations } from 'next-intl'

import { AdaptedProduct, Context, Translator } from '@/types'
import {
  DenseDefault,
  PortraitDefault,
  RotorDefault,
} from '@/components/templates'
import { RotorReferences } from '@/components/templates/RotorReferences'
import { OperationalResourceSheetsDocument } from '@/features/project/reportingDocuments/OperationalResourceSheetsDocument'
import { getNbrOfItemsPerPage } from '@/utils'
import { buildContentFallbackLocales } from '@/utils/languagePolicy'
import { useOptions } from '@/hooks'

import { Summary } from './Summary'
import { Header } from './Header'
import { RotorFooter, SkopFooter } from './Footers'
import { rotorReferencesStyles, rotorStyles, styles } from './styles'
import { formatItems } from './utils'

type PreviewPage = {
  id: number
  items: AdaptedProduct[]
}

type SheetTemplateProps = {
  data: AdaptedProduct[]
  context: Context
  locale: string
  fallbackLocales: string[]
  template: 'sheet' | 'dense' | 'rotor' | 'references'
}

const SheetTemplate = ({
  data,
  context,
  locale,
  fallbackLocales,
  template,
}: SheetTemplateProps) => {
  const { customSettings: settings = {} } = context
  const settingsName = typeof settings.name === 'string' ? settings.name : ''
  const itemsPerPage = getNbrOfItemsPerPage(template)

  const pages = useMemo(() => {
    if (settings?.generateSummary) return data

    const nbrItems = data.length
    const nbrPages = Math.ceil(nbrItems / itemsPerPage)
    const newPages: PreviewPage[] = []

    for (let i = 0; i < nbrPages; i++) {
      const page = {
        id: i,
        items: data.slice(i * itemsPerPage, (i + 1) * itemsPerPage),
      }
      newPages.push(page)
    }
    return newPages
  }, [data, itemsPerPage, settings?.generateSummary])

  const documentProps = useMemo(
    () => ({
      title: `Catalogue Ressources ${settingsName}`,
      subject: `Catalogue Ressources ${settingsName} - réalisé avec Skop`,
      author: settingsName || 'Skop',
      producer: 'Skop',
      creator: 'Skop',
      language: locale,
    }),
    [locale, settingsName],
  )

  return (
    <Document {...documentProps}>
      {!!settings.generateSummary && (
        <Summary data={data} settings={settings} />
      )}
      {pages.map((page) => {
        const items = page.items?.map((item) =>
          formatItems({ item, context, locale, fallbackLocales }),
        )
        return items?.map((item, index) => (
          <Page
            orientation="portrait"
            style={styles.page}
            key={`page-${page.id}-${index}`}
          >
            <Header settings={settings} />
            <PortraitDefault
              key={`page-${page.id}-${index}`}
              category={page.name}
              item={item}
              settings={settings}
            />
            <SkopFooter settings={settings} />
          </Page>
        ))
      })}
    </Document>
  )
}

type DenseTemplateProps = {
  data: AdaptedProduct[]
  context: Context
  locale: string
  fallbackLocales: string[]
  template: 'sheet' | 'dense' | 'rotor' | 'references'
}

const DenseTemplate = ({
  data,
  context,
  locale,
  fallbackLocales,
  template,
}: DenseTemplateProps) => {
  const { customSettings: settings = {} } = context
  const settingsName = typeof settings.name === 'string' ? settings.name : ''
  const itemsPerPage = getNbrOfItemsPerPage(template)

  const pages = useMemo(() => {
    if (settings?.generateSummary) return data

    const nbrItems = data.length
    const nbrPages = Math.ceil(nbrItems / itemsPerPage)
    const newPages: PreviewPage[] = []

    for (let i = 0; i < nbrPages; i++) {
      const page = {
        id: i,
        items: data.slice(i * itemsPerPage, (i + 1) * itemsPerPage),
      }
      newPages.push(page)
    }
    return newPages
  }, [data, itemsPerPage, settings?.generateSummary])

  const documentProps = useMemo(
    () => ({
      title: `Catalogue Ressources ${settingsName}`,
      subject: `Catalogue Ressources ${settingsName} - réalisé avec Skop`,
      author: settingsName || 'Skop',
      producer: 'Skop',
      creator: 'Skop',
      language: locale,
    }),
    [locale, settingsName],
  )

  return (
    <Document {...documentProps}>
      {!!settings?.generateSummary && (
        <Summary data={data} settings={settings} />
      )}
      {pages.map((page, index) => {
        const items = page.items?.map((item) =>
          formatItems({ item, context, locale, fallbackLocales }),
        )
        return (
          <Page
            orientation="portrait"
            style={styles.page}
            key={`page-${page.id}-${index}`}
          >
            <Header settings={settings} />
            <DenseDefault
              key={`page-${page.id}-${index}`}
              category={page.name}
              items={items}
              settings={settings}
            />
            <SkopFooter settings={settings} />
          </Page>
        )
      })}
    </Document>
  )
}

type RotorTemplateProps = {
  data: AdaptedProduct[][]
  context: Context
  locale: string
  fallbackLocales: string[]
  t: Translator
  date?: string
  firstPageNumber?: number
  rotorFooterLabel?: string
}

const RotorTemplate = ({
  data,
  context,
  locale,
  fallbackLocales,
  t,
  date,
  firstPageNumber = 1,
  rotorFooterLabel,
}: RotorTemplateProps) => {
  const { customSettings = {} } = context
  const name =
    typeof customSettings.name === 'string' ? customSettings.name : ''
  const binding =
    typeof customSettings.binding === 'number' ? customSettings.binding : 0

  const project = useMemo(() => data?.[0]?.[1]?.projectID?.label, [data])

  const newPages = useMemo(
    () =>
      data.map((page, index) => ({
        id: index + 1,
        items: page,
      })),
    [data],
  )

  const documentProps = useMemo(
    () => ({
      title: `Catalogue Ressources ${name || ''}`,
      subject: 'Inventaire des éléments réutilisables',
      author: name || 'Skop',
      producer: name || 'Skop',
      creator: name || 'Skop',
      language: locale,
    }),
    [locale, name],
  )

  return (
    <Document {...documentProps}>
      {newPages.map(({ id, items }) => (
        <Page
          orientation="portrait"
          style={{
            ...rotorStyles.page,
            ...(binding > 0
              ? id % 2 === 1
                ? { paddingLeft: binding }
                : { paddingRight: binding }
              : {}),
          }}
          key={`page-${id}`}
        >
          <RotorDefault
            key={`page-${id}`}
            items={items}
            project={project ?? ''}
            locale={locale}
            fallbackLocales={fallbackLocales}
            context={context}
          />
          <RotorFooter
            t={t}
            project={project}
            template="rotor"
            date={date}
            firstPageNumber={firstPageNumber}
            footerLabelOverride={rotorFooterLabel}
          />
        </Page>
      ))}
    </Document>
  )
}

type RotorReferencesTemplateProps = {
  data: AdaptedProduct[]
  context: Context
  locale: string
  fallbackLocales: string[]
  t: Translator
  date?: string
  firstPageNumber?: number
  rotorFooterLabel?: string
}

const RotorReferencesTemplate = ({
  data,
  context,
  locale,
  fallbackLocales,
  t,
  date,
  firstPageNumber = 1,
  rotorFooterLabel,
}: RotorReferencesTemplateProps) => {
  const { customSettings = {} } = context
  const name =
    typeof customSettings.name === 'string' ? customSettings.name : ''
  const binding =
    typeof customSettings.binding === 'number' ? customSettings.binding : 0
  const template = 'references'
  const itemsPerPage = getNbrOfItemsPerPage(template)

  const project = useMemo(() => data?.[0]?.projectID?.label, [data])

  const newPages = useMemo(() => {
    const nbrItems = data.length
    const nbrPages = Math.ceil(nbrItems / itemsPerPage)
    const pages: PreviewPage[] = []

    for (let i = 0; i < nbrPages; i++) {
      const page = {
        id: i + 1,
        items: data.slice(i * itemsPerPage, (i + 1) * itemsPerPage),
      }
      pages.push(page)
    }
    return pages
  }, [data, itemsPerPage])

  const documentProps = useMemo(
    () => ({
      title: 'FICHES DÉTAILLÉES',
      subject: 'Inventaire des éléments réutilisables',
      author: name || 'Skop',
      producer: name || 'Skop',
      creator: name || 'Skop',
      language: locale,
    }),
    [locale, name],
  )

  const getBindingStyle = (pageId: number) => ({
    ...(binding > 0
      ? pageId % 2 === 1
        ? { paddingLeft: binding }
        : { paddingRight: binding }
      : {}),
  })

  return (
    <Document {...documentProps}>
      {newPages.map((page, index) => (
        <Page
          orientation="portrait"
          style={{
            ...rotorReferencesStyles.page,
            ...getBindingStyle(page.id),
          }}
          key={`page-${page.id}`}
        >
          <RotorReferences
            key={`page-${page.id}`}
            page={page}
            locale={locale}
            fallbackLocales={fallbackLocales}
            isLastPage={index === newPages.length - 1}
            t={t}
          />
          <RotorFooter
            t={t}
            project={project}
            template={template}
            date={date}
            firstPageNumber={firstPageNumber}
            footerLabelOverride={rotorFooterLabel}
          />
        </Page>
      ))}
    </Document>
  )
}

type TemplateKey =
  | 'sheet'
  | 'dense'
  | 'rotor'
  | 'references'
  | 'operational-resource-sheets'

type CatalogData = AdaptedProduct[] | AdaptedProduct[][]

type ViewerProps = {
  data: CatalogData
  date?: string
  template?: string
  locale?: string
  supportedLocales?: string[]
  defaultLocale?: string
  projectDefaultLocale?: string
  firstPageNumber?: number
  rotorFooterLabel?: string
}

type CatalogPdfDocumentProps = ViewerProps & {
  context: Context
  contentLocale: string
  fallbackLocales: string[]
  templateToUse: TemplateKey
  t: Translator
}

const isRotorCatalogData = (data: CatalogData): data is AdaptedProduct[][] =>
  data.length > 0 && Array.isArray(data[0])

const toRotorCatalogData = (data: CatalogData): AdaptedProduct[][] => {
  if (data.length === 0) return []
  return isRotorCatalogData(data) ? data : [data]
}

const toFlatCatalogData = (data: CatalogData): AdaptedProduct[] =>
  isRotorCatalogData(data) ? data.flat() : data

export const CatalogPdfDocument = ({
  data,
  date,
  firstPageNumber,
  rotorFooterLabel,
  context,
  contentLocale,
  fallbackLocales,
  templateToUse,
  t,
}: CatalogPdfDocumentProps) => {
  if (templateToUse === 'operational-resource-sheets') {
    return (
      <OperationalResourceSheetsDocument
        products={toFlatCatalogData(data)}
        context={context}
        date={date}
        locale={contentLocale}
      />
    )
  }

  if (templateToUse === 'rotor') {
    return (
      <RotorTemplate
        data={toRotorCatalogData(data)}
        context={context}
        locale={contentLocale}
        fallbackLocales={fallbackLocales}
        t={t}
        date={date}
        firstPageNumber={firstPageNumber}
        rotorFooterLabel={rotorFooterLabel}
      />
    )
  }

  if (templateToUse === 'references') {
    return (
      <RotorReferencesTemplate
        data={toFlatCatalogData(data)}
        context={context}
        locale={contentLocale}
        fallbackLocales={fallbackLocales}
        t={t}
        date={date}
        firstPageNumber={firstPageNumber}
        rotorFooterLabel={rotorFooterLabel}
      />
    )
  }

  if (templateToUse === 'dense') {
    return (
      <DenseTemplate
        data={toFlatCatalogData(data)}
        context={context}
        locale={contentLocale}
        fallbackLocales={fallbackLocales}
        template={templateToUse}
      />
    )
  }

  return (
    <SheetTemplate
      data={toFlatCatalogData(data)}
      context={context}
      locale={contentLocale}
      fallbackLocales={fallbackLocales}
      template={templateToUse}
    />
  )
}

const resolveTemplateKey = (value: unknown): TemplateKey => {
  if (
    value === 'dense' ||
    value === 'rotor' ||
    value === 'references' ||
    value === 'operational-resource-sheets'
  ) {
    return value
  }
  return 'sheet'
}

export type CatalogPdfDocumentElement = ReactElement<DocumentProps>

type CatalogPdfDocumentState = {
  document: CatalogPdfDocumentElement | null
  isReady: boolean
}

export const useCatalogPdfDocumentState = ({
  data,
  date,
  template,
  locale,
  supportedLocales,
  defaultLocale,
  projectDefaultLocale,
  firstPageNumber,
  rotorFooterLabel,
}: ViewerProps): CatalogPdfDocumentState => {
  const routeLocale = useLocale()
  const context = useOptions()
  const t = useTranslations()

  const contentLocale = locale ?? routeLocale
  const templateToUse = resolveTemplateKey(
    template || context?.customSettings?.template,
  )
  const fallbackLocales = useMemo(
    () =>
      buildContentFallbackLocales({
        activeLocale: contentLocale,
        catalogDefaultLocale: defaultLocale,
        projectDefaultLocale,
        teamLanguages: context?.customSettings?.languages,
        supportedLocales,
      }),
    [
      contentLocale,
      context?.customSettings?.languages,
      defaultLocale,
      projectDefaultLocale,
      supportedLocales,
    ],
  )

  const isReady = Boolean(context?.isDone)
  const document = useMemo(() => {
    if (!context?.isDone) return null

    return (
      <CatalogPdfDocument
        data={data}
        context={context}
        contentLocale={contentLocale}
        fallbackLocales={fallbackLocales}
        templateToUse={templateToUse}
        date={date}
        t={t}
        firstPageNumber={firstPageNumber}
        rotorFooterLabel={rotorFooterLabel}
      />
    )
  }, [
    contentLocale,
    context,
    data,
    date,
    fallbackLocales,
    firstPageNumber,
    rotorFooterLabel,
    t,
    templateToUse,
  ])

  return { document, isReady }
}

const Viewer = (props: ViewerProps) => {
  const { document } = useCatalogPdfDocumentState(props)

  return <PDFViewer>{document ?? <Document />}</PDFViewer>
}

export default Viewer
